// src/components/EntryHistory.jsx
import React from 'react';
import { HistoryGrid } from '@1823-partners/core';
import { fetchEntry } from '../api';
import { formatDate } from '../utils';

const FIELD_NAMES = {
  DIRECTION: 'Direction',
  DATE: 'Trade Date',
  CUSIP: 'CUSIP',
  CURRENCY: 'Currency',
  SEC_OWNER: 'Security Owner',
  SEC_OWNER_TEAM: 'Security Owner Team',
  TYPE: 'Type',
  COUNTERPARTY: 'Counterparty',
  CONNECTED_ENTITY: 'Connected Entity',
  ILLIQUID: 'Illiquid (Y/N)',
  FLOATER: 'Floater (Y/N)',
  DESCRIPTION: 'Description',
  ORIGINAL_FACE: 'Original Face',
  CURRENT_FACE: 'Current Face',
  PRICE: 'Price',
  EST_PROCEEDS: 'Est Proceeds',
  MARKET_SPREAD: 'Market Convention Spread',
  TREAS_SPREAD: 'Treasury Equivalent Spread',
  BASE_YIELD: 'Base Case Yield',
  WAL: 'WAL',
  DURATION: 'Duration',
  OVERRIDE: 'Override (Y/N)',
  RATING: 'Rating at Trade',
  CURR_CREDIT_SUPPORT: 'Current Credit Support',
  SSFA_DETACHMENT_POINT: 'SSFA Detachment Point',
  THICKNESS: 'Thickness',
  ACCOUNT: 'Account',
  NOTES: 'Notes',
};

const boolFormatter = (params) => {
  if (params.value === true) return 'Y';
  if (params.value === false) return 'N';
  return '';
};

const currencyFormatter = (params) => {
  if (params.value == null) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(params.value);
};

const COLUMNS = [
  {
    headerName: 'Direction',
    field: 'DIRECTION',
    pinned: 'left',
  },
  {
    headerName: 'Trade Date',
    field: 'DATE',
    pinned: 'left',
  },
  {
    headerName: 'CUSIP',
    field: 'CUSIP',
    pinned: 'left',
  },
  {
    headerName: 'Currency',
    field: 'CURRENCY',
  },
  {
    headerName: 'Security Owner',
    field: 'SEC_OWNER',
  },
  {
    headerName: 'Security Owner Team',
    field: 'SEC_OWNER_TEAM',
  },
  {
    headerName: 'Type',
    field: 'TYPE',
  },
  {
    headerName: 'Counterparty',
    field: 'COUNTERPARTY',
  },
  {
    headerName: 'Connected Entity',
    field: 'CONNECTED_ENTITY',
  },
  {
    headerName: 'Illiquid (Y/N)',
    field: 'ILLIQUID',
    valueFormatter: boolFormatter,
  },
  {
    headerName: 'Floater (Y/N)',
    field: 'FLOATER',
    valueFormatter: boolFormatter,
  },
  {
    headerName: 'Description',
    field: 'DESCRIPTION',
  },
  {
    headerName: 'Original Face',
    field: 'ORIGINAL_FACE',
    type: 'numericColumn',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Current Face',
    field: 'CURRENT_FACE',
    type: 'numericColumn',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Price',
    field: 'PRICE',
    type: 'numericColumn',
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Est Proceeds',
    field: 'EST_PROCEEDS',
    type: 'numericColumn',
    editable: false,
    valueGetter: (params) => {
      const price = params.data.PRICE;
      const currentFace = params.data.CURRENT_FACE;
      const direction = params.data.DIRECTION;
      if (price == null || currentFace == null || !direction) return null;
      const multiplier = direction.toUpperCase() === 'BUY' ? 1 : -1;
      return ((price * currentFace) / 100) * multiplier;
    },
    valueFormatter: currencyFormatter,
  },
  {
    headerName: 'Market Convention Spread',
    field: 'MARKET_SPREAD',
  },
  {
    headerName: 'Treasury Equivalent Spread',
    field: 'TREAS_SPREAD',
  },
  {
    headerName: 'Base Case Yield',
    field: 'BASE_YIELD',
    type: 'numericColumn',
    valueFormatter: (params) => {
      if (params.value == null) return '';
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(params.value);
    },
  },
  {
    headerName: 'WAL',
    field: 'WAL',
    type: 'numericColumn',
    valueFormatter: (params) => {
      if (params.value == null) return '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(params.value);
    },
  },
  {
    headerName: 'Duration',
    field: 'DURATION',
  },
  {
    headerName: 'Override (Y/N)',
    field: 'OVERRIDE',
    valueFormatter: boolFormatter,
  },
  {
    headerName: 'Rating at Trade',
    field: 'RATING',
  },
  {
    headerName: 'Current Credit Support',
    field: 'CURR_CREDIT_SUPPORT',
  },
  {
    headerName: 'SSFA Detachment Point',
    field: 'SSFA_DETACHMENT_POINT',
  },
  {
    headerName: 'Thickness',
    field: 'THICKNESS',
  },
  {
    headerName: 'Account',
    field: 'ACCOUNT',
  },
  {
    headerName: 'Notes',
    field: 'NOTES',
  },
];

const EntryHistory = ({ entryId }) => (
  <HistoryGrid
    fetchHistory={() => fetchEntry(entryId)}
    fetchParams={[entryId]}
    fieldNames={FIELD_NAMES}
    columns={COLUMNS}
    transformData={(data) =>
      data.map((row) => ({ ...row, DATE: formatDate(row.DATE) }))
    }
  />
);

export default EntryHistory;
